import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Divider,
  Grid2,
  IconButton,
  InputAdornment,
  Menu,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { MdOutlineWatchLater } from "react-icons/md";
import PhysicalProgressSCurve from "./PhysicalProgressSCurve";
import FinancialProgressSCurve from "./FinancialProgressSCurve";
import EarnedValueManagementProjectLanding from "./EarnedValueManagementProjectLanding";
import Productivity from "./Productivity";
import MonthlyHighlights from "./MonthlyHighlights";
import EPCCompletion from "./EPCCompletion";
import CriticalActivities from "./CriticalActivities";
import { useGetRiskKPIQuery } from '../../../store/slices/apis/riskDashboardSlice';
import { useGetIssuesKPIQuery } from '../../../store/slices/apis/issuesDashboardSlice';
import { useGetPlanVsActualQuery } from "store/slices/apis/scheduleDashboarSlice";
import { useNavigate, useParams } from "react-router-dom";
import { useGetProjectSummaryQuery } from "store/slices/apis/projectHomeDetailSlice";
import moment from "moment";
import ProjectHomePhysicalProgress from "./PhysicalProgress";
import ProjectLandingSchedulePerformanceIndicator from "./ProjectLandingSchedulePerformanceIndicator";
import ProjectLandingCostPerformanceIndicator from "./ProjectLandingCostPerformanceIndicator";
import ProjectLandingFinancialProgress from "./ProjectLandingFinancialProgress";
import { useGetProjectDetailQuery } from "store/slices/apis/projectApiSlice";
import MilestonesBarChart from "./MilestonesBarChart";
import MilestonesAndRisksTable from "./MilestonesAndRisksTable";
import { useGetPlanActualQuery } from "store/slices/apis/costSlice";
import { ReactComponent as TimeOutlineBlack } from "assets/PwC_Funct_Icons_Time_Outline_Black_RGB.svg";

const ProjectLandingDashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [earnedValueMan, setEarnedValueMan] = useState(null);
  // Physical Progress - Plan vs Actual
  const { data, isLoading } = useGetPlanVsActualQuery({ projectId });
  const { data: projectDetails, isLoading: detailsLoading } = useGetProjectSummaryQuery(projectId);
  const { data: projectInfo, isLoading: projectLoading } = useGetProjectDetailQuery(projectId);
  const actualPlanRes = useGetPlanActualQuery({ projectId });

  const projectDesc = projectInfo?.data;

  const actual = data?.actualPercentage ?? 0;
  const planned = data?.plannedPercentage ?? 0;
  const overall = data?.overallTarget ?? 100;
  // Physical Progress - Plan vs Actual

  const projectData = projectDetails?.data

  const programsCards = [
    {
      title: projectData?.contractvalue ? `₹ ${projectData?.contractvalue} Cr` : "₹ 0",
      subtitle: "Contract Value",
    },
    {
      title: projectData?.estimateatcompletion ? `₹ ${projectData?.estimateatcompletion} Cr` : "₹ 0",
      subtitle: "Estimate at Completion",
    },
    {
      title: projectData?.forecastcompletiondate ? moment(projectData?.forecastcompletiondate).format("Do MMMM, YYYY") : "-",
      subtitle: "Forecast Completion Date",
    },
    {
      title: `${(projectData?.actualmanpower ?? 0)} / ${(projectData?.planmanpower ?? 0)}`,
      subtitle: "Actual VS Planned Manpower ",
    },
    {
      title: projectData?.totalinvoicevalue ? `₹ ${projectData?.totalinvoicevalue} Cr` : "₹ 0",
      subtitle: "Total Invoice Submitted Value",
    },
    {
      title: projectData?.activerisk ?? 0,
      subtitle: "High Risks",
    },
    {
      title: projectData?.openissues ?? 0,
      subtitle: "Open Issues",
    },
    {
      title: projectData?.milestoneProgress ?? 0,
      subtitle: "Milestone Achieved",
    },
  ];

  const handleCardClick = (subtitle) => {
    if (subtitle === "Milestone Achieved") {
      localStorage.setItem("scheduleTabIndex", 2);
      navigate(`/projects/${projectId}?type=schedule`);
    } else if (subtitle === "High Risks") {
      localStorage.setItem("riskTabIndex", 2);
      navigate(`/projects/${projectId}?type=risk`);
    } else if (subtitle === "Open Issues") {
      localStorage.setItem("riskTabIndex", 0);
      navigate(`/projects/${projectId}?type=risk`);
    }
  };

  const projectCards = [
    {
      title: projectData?.contractorname ?? "-",
      subtitle: "Contractor Name",
    },
    {
      title: projectData?.startdate ? moment(projectData?.startdate).format("Do MMMM, YYYY") : "-",
      subtitle: "Start Date",
    },
    {
      title: projectData?.enddate ? moment(projectData?.enddate).format("Do MMMM, YYYY") : "-",
      subtitle: "Completion Date",
    },
  ];



  return (
    <>
      <Box>
        <Grid2 container spacing={1} sx={{ mt: 2 }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "flex-start" },
                textAlign: { xs: "center", md: "left" },
                mt: "25px",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#696969",
                  letterSpacing: "-0.4px",
                  mb: "5px",
                }}
              >
                Contractor Number
              </Typography>
              <Chip
                label={projectDesc?.contractor_number ?? "Not available"}
                sx={{
                  background: "#415385",
                  fontSize: "14px",
                  lineHeight: "16px",
                  fontWeight: "400",
                  color: "#FFFFFF",
                  letterSpacing: "-0.4px",
                  width: { xs: "100%", md: "auto" },
                  "&.MuiChip-root": {
                    height: "24px",
                  },
                }}
              />
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 10 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="stretch"
              sx={{ mt: "25px" }}
            >
              {projectCards?.map((val) => {
                return (
                  <Grid2 size={{ xs: 12, md: 4 }}>
                    <Card
                      sx={{
                        boxShadow: "none",
                        borderRadius: "4px",
                        bgcolor: "#FFFFFF",
                      }}
                    >
                      <CardHeader
                        avatar={
                          <Avatar
                            sx={{
                              bgcolor: "#cfd3dd",
                            }}
                            aria-label="recipe"
                          >
                            {/* <MdOutlineWatchLater style={{ color: "#474747" }} /> */}
                            <TimeOutlineBlack style={{ height:"25", width:"25px", fill:"#474747",stroke:"#474747", strokeWidth:2 }} />
                          </Avatar>
                        }
                        sx={{
                          padding: "10px",
                          marginRight: "10px",
                          "& .MuiCardHeader-title": {
                            fontSize: "16px",
                            fontWeight: 600,
                            lineHeight: "24px",
                            letterSpacing: "-0.4px",
                            color: "#474747",
                          },
                          "& .MuiCardHeader-subheader": {
                            fontSize: "14px",
                            fontWeight: 400,
                            lineHeight: "24px",
                            letterSpacing: "-0.4px",
                            color: "#696969",
                          },
                        }}
                        title={val?.title ?? "-"}
                        subheader={val?.subtitle ?? "-"}
                      />
                    </Card>
                  </Grid2>
                );
              })}
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Card
                  sx={{
                    boxShadow: "none",
                    borderRadius: "4px",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar
                        sx={{
                          fontSize: "16px",
                          fontWeight: 500,
                          lineHeight: "24px",
                          letterSpacing: "-0.4px",
                          bgcolor: "#415385",
                        }}
                        aria-label="recipe"
                      >
                        {(projectData?.projectmanager || '')
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase())
                          .join('')
                          .slice(0, 2)}
                      </Avatar>
                    }
                    sx={{
                      padding: "10px",
                      "& .MuiCardHeader-title": {
                        fontSize: "16px",
                        fontWeight: 600,
                        lineHeight: "24px",
                        letterSpacing: "-0.4px",
                        color: "#474747",
                      },
                      "& .MuiCardHeader-subheader": {
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "24px",
                        letterSpacing: "-0.4px",
                        color: "#696969",
                      },
                    }}
                    title={projectData?.projectmanager ?? "-"}
                    subheader={"Project Manager"}
                  />
                </Card>
              </Grid2>
            </Stack>
          </Grid2>
        </Grid2>
      </Box>
      <Divider
        sx={{ borderColor: "#fff", borderBottomWidth: "initial", mt: 2 }}
      />

      <Box>
        <Grid2 container spacing={1} sx={{ mt: 2 }}>
          <Grid2 size={{ xs: 12, md: 12 }} sx={{ mt: 2 }}>
            <Grid2
              xs={12}
              md={12}
              sx={{
                display: { xs: "grid", md: "flex" },
                alignItems: "flex-start",
                gap: "20px",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontSize: "20px",
                  fontWeight: "500",
                  color: "#252525",
                  letterSpacing: "-0.4px",
                }}
              >
                Project Description
              </Typography>
              <Chip
                label={`Project Type: ${projectDesc?.project_type ?? "Not available"}`}
                sx={{
                  background: "#415385",
                  fontSize: "14px",
                  lineHeight: "16px",
                  fontWeight: "400",
                  color: "#FFFFFF",
                  letterSpacing: "-0.4px",
                  lineHeight: "100%",
                  "&.MuiChip-root": {
                    height: "24px",
                  },
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 12 }} sx={{ mt: "6px", mb: "20px" }}>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: "400",
                  color: "#182A4E80",
                  letterSpacing: "-0.4px",
                }}
              >
                {projectDesc?.discription ?? "No description available"}
              </Typography>
            </Grid2>
          </Grid2>
          {programsCards?.map((val) => {
            const subtitle = val.subtitle;
            const isClickable = ["Milestone Achieved", "High Risks", "Open Issues"].includes(subtitle);
            return (
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Card
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #25252517",
                    borderRadius: "3px",
                    cursor: isClickable ? "pointer" : "default",
                  }}
                  onClick={() => isClickable && handleCardClick(subtitle)}
                >
                  <CardHeader
                    avatar={
                      <Avatar
                        sx={{
                         bgcolor: "#9ED3CC",
                          borderRadius: "initial",
                          "& svg": {
                            display: "none", // hide any default SVG icon
                          },
                        }}
                        aria-label="recipe"
                      ></Avatar>
                    }
                    sx={{
                      "& .MuiCardHeader-title": {
                        fontSize: "18px",
                        fontWeight: 700,
                        lineHeight: "100%",
                        letterSpacing: "-0.4px",
                        color: "#252525",
                        marginBottom: 1,
                      },
                      "& .MuiCardHeader-subheader": {
                        fontSize: "13px",
                        fontWeight: 500,
                        lineHeight: "100%",
                        letterSpacing: "-0.4px",
                        color: "#25252580",
                      },
                    }}
                    title={val?.title ?? "-"}
                    subheader={val?.subtitle ?? "-"}
                  />
                </Card>
              </Grid2>
            );
          })}
          <Grid2 container spacing={2} sx={{ mt: 2 }}>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <ProjectHomePhysicalProgress
              // data={[
              //   {
              //     name: "Overall",
              //     data: [
              //       {
              //         y: overall,
              //         color: "#E9E3E3",
              //         radius: "90%",
              //         innerRadius: "100%",
              //       },
              //     ],
              //   },
              //   {
              //     name: "Plasdan",
              //     data: [
              //       {
              //         y: planned,
              //         color: "#415385",
              //         radius: "75%",
              //         innerRadius: "85%",
              //       },
              //     ],
              //   },
              //   {
              //     name: "Actual",
              //     data: [
              //       {
              //         y: actual,
              //         color: actual >= planned ? '#3E756D' : '#D44A20',
              //         radius: "60%",
              //         innerRadius: "70%",
              //       },
              //     ],
              //   },
              // ]}
              // loading={isLoading}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 3 }}>
              <ProjectLandingFinancialProgress
                data={[
                  {
                    name: "Overall",
                    data: [
                      {
                        y: 100,
                        color: "#E9E3E3",
                        radius: "90%",
                        innerRadius: "100%",
                      },
                    ],
                  },
                  {
                    name: "Plan",
                    data: [
                      {
                        y: actualPlanRes?.data?.data?.planPercent ?? 0,
                        color: "#295477",
                        radius: "75%",
                        innerRadius: "85%",
                      },
                    ],
                  },
                  {
                    name: "Actual",
                    data: [
                      {
                        y: actualPlanRes?.data?.data?.actualPercent ?? 0,
                        color: "#E72B33",
                        radius: "60%",
                        innerRadius: "70%",
                      },
                    ],
                  },
                ]}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <ProjectLandingSchedulePerformanceIndicator />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <ProjectLandingCostPerformanceIndicator cpiData={actualPlanRes?.data?.data} />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <PhysicalProgressSCurve />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <FinancialProgressSCurve earnValueData={setEarnedValueMan} />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <EarnedValueManagementProjectLanding earnValueData={earnedValueMan} />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              {/* <Productivity /> */}
              <MilestonesBarChart />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <MonthlyHighlights />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <EPCCompletion />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <CriticalActivities />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 12 }}>
              <MilestonesAndRisksTable />
            </Grid2>
          </Grid2>
        </Grid2>
      </Box>
    </>
  );
};

export default ProjectLandingDashboard;
